import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  exportFcdrClassicReport,
  getFcdrClassicReport,
  getFcdrOutcomeV2Report,
  type FcdrClassicQuery,
  type FcdrClassicResponse,
  type FcdrOutcomeV2Query,
  type FcdrOutcomeV2Response,
} from '../lib/api/native-operational-reports';
import { resolveAnalyticalYearPeriod } from '../lib/api/analytical-periods';
import { loadChartConstructor } from '../lib/third-party/chart';

type ChartInstance = { destroy: () => void };
type ChartConstructor = new (context: HTMLCanvasElement, config: Record<string, unknown>) => ChartInstance;

type FcdrView = 'classic' | 'outcome';
type OutcomeSubview = 'outcomes' | 'intelligence';

type ClassicFilters = {
  period: 'MONTH' | 'YTD' | 'FY_YTD';
  month: string;
  from: string;
  to: string;
  centre: string;
  ageing: string;
  search: string;
};

type CentreVisitColumn = 'ALL' | 'SECOND' | 'THIRD' | 'FOURTH' | 'FIFTH_PLUS';

type PatientTableQuery = {
  page: number;
  category: string;
  packageBill: string;
};

const PATIENT_PAGE_SIZE = 25;
const FCDR_PACKAGE_CHART_COLORS = [
  '#e91e63', '#8b5cf6', '#0ea5e9', '#14b8a6', '#f59e0b', '#f97316',
  '#64748b', '#ec4899', '#22c55e', '#6366f1', '#ef4444',
];

const FCDR_DROPOUT_STATUS_COLORS = [
  '#e91e63', '#8b5cf6', '#ef4444', '#f97316', '#f59e0b', '#0ea5e9',
  '#14b8a6', '#64748b',
];

function FcdrChartCanvas({ id, config }: { id: string; config: Record<string, unknown> }) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const chart = useRef<ChartInstance | null>(null);

  useEffect(() => {
    if (!canvas.current) return undefined;
    let disposed = false;
    void loadChartConstructor().then((Chart) => {
      if (disposed || !canvas.current) return;
      chart.current?.destroy();
      chart.current = new (Chart as ChartConstructor)(canvas.current, config);
    });
    return () => {
      disposed = true;
      chart.current?.destroy();
      chart.current = null;
    };
  }, [config]);

  return <canvas id={id} ref={canvas} />;
}

type OutcomeFilters = {
  period: 'MONTH' | 'YTD' | 'FY_YTD';
  month: string;
  from: string;
  to: string;
  centre: string;
  outcome: string;
  returnInterval: string;
  attention: string;
  search: string;
};

type OutcomePatientTableFilters = {
  centre: string;
  status: 'ALL' | 'ACTIVE' | 'DROPOUT' | 'CONVERTED';
  search: string;
};

type FcdrKpiTone = 'blue' | 'teal' | 'amber' | 'orange' | 'violet' | 'cyan' | 'green' | 'pink';

type FcdrDataState = 'NOT_LOADED' | 'LOADING' | 'READY' | 'PARTIAL_DATA' | 'NO_DATA' | 'ERROR';

const classicKpis: Array<{ title: string; tone: FcdrKpiTone }> = [
  { title: 'Total Registered', tone: 'blue' },
  { title: '30 Days', tone: 'teal' },
  { title: '60 Days', tone: 'amber' },
  { title: '90 Days', tone: 'orange' },
  { title: '90+ Days', tone: 'violet' },
  { title: 'Active Follow-up', tone: 'cyan' },
  { title: 'Converted', tone: 'green' },
  { title: 'Dropout', tone: 'pink' },
];

const placeholderVisitNumbers = [1, 2, 3, 4, 5];

function ordinalVisit(visitNumber: number) {
  const remainder = visitNumber % 100;
  const suffix = remainder >= 11 && remainder <= 13
    ? 'th'
    : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[visitNumber % 10] || 'th';
  return `${visitNumber}${suffix}`;
}

function visitStageLabel(tone: 'dropout' | 'conversion', visitNumber: number) {
  if (visitNumber === 1) return tone === 'dropout' ? 'No 2nd visit' : 'Before 2nd visit';
  return `After ${ordinalVisit(visitNumber)} visit`;
}

function shortCentreName(value: string) {
  return value.replace(/^Ferty9 Fertility Center\s*-\s*/i, '').trim();
}

function reportDate(value: string | null | undefined) {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return 'the report date';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))));
}

function classicCategoryLabel(value: string | null) {
  if (!value) return '—';
  return value === 'ACTIVE_FOLLOW_UP' ? 'Active Follow-up' : value.replaceAll('_', ' ');
}

const centreBreakdownColumns = [
  'Centre', 'Total', '2nd visit', '3rd visit', '4th visit', '5th+ visit',
  '0–30 days', '31–60 days', '61–90 days', '90+ days', 'Active Follow-up', 'Converted', 'Dropout',
];

const patientDetailColumns = [
  'Patient ID', 'Patient', 'Centre', 'Registration (import date)', 'First appointment',
  'Latest appointment', '2nd visit', '3rd visit',
  '4th visit', '5th+ visit', 'Package bill', 'Days to 2nd visit / report date', 'Category',
];

const patientDetailColumnClassNames = [
  'fcdr-patient-id', 'fcdr-patient-name', 'fcdr-patient-centre', 'fcdr-patient-date',
  'fcdr-patient-date', 'fcdr-patient-date', 'fcdr-patient-date', 'fcdr-patient-date',
  'fcdr-patient-date', 'fcdr-patient-date', 'fcdr-patient-package', 'fcdr-patient-days',
  'fcdr-patient-category',
];

const outcomeKpis: Array<{ title: string; description: string; tone: FcdrKpiTone }> = [
  { title: 'Total Registered', description: 'Patients registered in the selected period', tone: 'blue' },
  { title: 'Active', description: 'Patients active in care and not converted', tone: 'teal' },
  { title: 'Dropout', description: '90+ days without an attended visit', tone: 'pink' },
  { title: 'Converted', description: 'Patients with a qualifying package bill', tone: 'green' },
];

const intelligenceKpis: Array<{ title: string; description: string; tone: FcdrKpiTone }> = [
  { title: 'Registered', description: 'Selected registration period', tone: 'blue' },
  { title: 'Active', description: 'Currently active, non-converted outcome cohort', tone: 'teal' },
  { title: 'Returned for 2nd Visit', description: 'Patients reaching a second attended visit', tone: 'violet' },
  { title: 'Converted', description: 'Qualifying package bill recorded', tone: 'green' },
  { title: 'Dropout', description: '90+ days without an attended visit', tone: 'pink' },
];

const outcomeCentreColumns = [
  'Centre', 'Total Registered', 'Active', 'Dropout', 'Converted',
];

const outcomeCentreColumnClassNames = [
  'fcdr-centre-name', 'fcdr-numeric', 'fcdr-numeric', 'fcdr-numeric', 'fcdr-numeric',
];

const outcomePatientColumns = [
  'Patient ID', 'Patient', 'Centre', 'Registration', 'Care activity',
  'Package bill', 'Status', 'Consideration',
];

const outcomePatientColumnClassNames = [
  'fcdr-outcome-patient-id',
  'fcdr-outcome-patient-name',
  'fcdr-outcome-patient-centre',
  'fcdr-outcome-patient-registration',
  'fcdr-outcome-patient-care',
  'fcdr-outcome-patient-package',
  'fcdr-outcome-patient-status',
  'fcdr-outcome-patient-consideration',
];

const intelligenceCentreColumns = [
  'Centre', 'Registered', 'Returned for 2nd visit', 'Half returned within',
  'Active', 'Dropout', 'Converted',
];

const returnIntervalLabels = [
  '0–7 days', '8–14 days', '15–30 days', '31–60 days',
  '61–90 days', '91+ days', 'No return recorded',
];

const currentMonth = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
};

const classicInitial = (): ClassicFilters => ({
  period: 'MONTH',
  month: currentMonth(),
  from: '',
  to: '',
  centre: '',
  ageing: 'ALL',
  search: '',
});

const outcomeInitial = (): OutcomeFilters => ({
  period: 'MONTH',
  month: currentMonth(),
  from: '',
  to: '',
  centre: '',
  outcome: 'ALL',
  returnInterval: 'ALL',
  attention: 'ALL',
  search: '',
});

const outcomePatientInitial = (): OutcomePatientTableFilters => ({
  centre: '',
  status: 'ALL',
  search: '',
});

function ReportIcon({ kind }: { kind: 'classic' | 'outcome' }) {
  return kind === 'classic' ? (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v14H4zM8 2v6m8-6v6M4 10h16M8 14h3m2 0h3m-8 3h3" /></svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 19V9m6 10V5m6 14v-7m4 7V3M3 19h18" /></svg>
  );
}

function FcdrDataStatus({ state, title }: { state: FcdrDataState; title?: string }) {
  const label = state === 'LOADING'
    ? 'Data Loading'
    : state === 'READY'
      ? 'Data Ready'
      : state === 'PARTIAL_DATA'
        ? 'Data Partial'
        : state === 'NO_DATA'
          ? 'No Data'
          : state === 'ERROR'
            ? 'Load Failed'
            : 'Data Not Loaded';
  const tone = state === 'READY'
    ? 'ready'
    : state === 'PARTIAL_DATA'
      ? 'partial'
      : state === 'NO_DATA' || state === 'ERROR'
        ? 'no-data'
        : 'pending';

  return <span className={`clinical-data-status ${tone}`} title={title}>{label}</span>;
}

function FcdrKpiCard({ title, description, detail, note, tone, value = '—' }: {
  title: string;
  description: string;
  detail?: string;
  note?: ReactNode;
  tone: FcdrKpiTone;
  value?: ReactNode;
}) {
  return (
    <article className={`fcdr-kpi-card ${tone}`}>
      <span className="fcdr-kpi-title">{title}</span>
      <strong className="fcdr-kpi-value">{value}</strong>
      <span className="fcdr-kpi-description">
        <strong>{description}</strong>
        {detail ? <span>{detail}</span> : null}
        {note ? <small>{note}</small> : null}
      </span>
    </article>
  );
}

function FcdrStageChart({ title, description, tone, rows }: {
  title: string;
  description: string;
  tone: 'dropout' | 'conversion';
  rows?: Array<{ label: string; count: number; percent: number }>;
}) {
  const stages = rows ?? placeholderVisitNumbers.map((visitNumber) => ({ label: visitStageLabel(tone, visitNumber), count: 0, percent: 0 }));
  const total = rows?.reduce((sum, row) => sum + row.count, 0) ?? 0;

  return (
    <article className={`fcdr-chart-card fcdr-clinical-insight-card ${tone}`}>
      <div className="fcdr-card-heading">
        <div><strong>{title}</strong><span>{description}</span></div>
        <span className="fcdr-pending-badge">{rows ? `${total} patients` : 'Awaiting data'}</span>
      </div>
      <div className="fcdr-stage-chart" role="img" aria-label={rows ? `${title}: ${total} patients` : `${title}: governed data connection pending`}>
        {stages.map((stage) => (
          <div className="fcdr-stage-row" key={stage.label}>
            <span>{stage.label}</span>
            <div className="fcdr-stage-track" aria-hidden="true"><i style={{ width: `${Math.max(0, Math.min(100, stage.percent))}%` }} /></div>
            <strong>{rows ? `${stage.count} · ${stage.percent.toFixed(1)}%` : '—'}</strong>
          </div>
        ))}
      </div>
      {!rows ? <div className="fcdr-chart-footnote">Chart structure is ready for governed FCDR values.</div> : null}
    </article>
  );
}

function FcdrDropoutStatusChart({ rows }: {
  rows?: FcdrClassicResponse['charts']['dropoutsByAppointmentStatus'];
}) {
  const displayed = rows ?? [{ key: 'pending', label: 'Awaiting data', count: 0, percent: 0 }];
  const total = rows?.reduce((sum, row) => sum + row.count, 0) ?? 0;

  return (
    <article className="fcdr-chart-card fcdr-clinical-insight-card dropout fcdr-dropout-status-card">
      <div className="fcdr-card-heading">
        <div><strong>Dropout Patients by Last Appointment Status</strong><span>Latest appointment evidence for patients without a second attended visit.</span></div>
        <span className="fcdr-pending-badge">{rows ? `${total} patients` : 'Awaiting data'}</span>
      </div>
      <div className="fcdr-stage-chart" role="img" aria-label={rows ? `Dropout patients by last appointment status: ${total} patients` : 'Dropout appointment status: governed data connection pending'}>
        {displayed.map((row, index) => (
          <div className="fcdr-stage-row" key={row.key}>
            <span>{row.label}</span>
            <div className="fcdr-stage-track" aria-hidden="true"><i style={{ width: `${Math.max(0, Math.min(100, row.percent))}%`, backgroundColor: FCDR_DROPOUT_STATUS_COLORS[index % FCDR_DROPOUT_STATUS_COLORS.length] }} /></div>
            <strong>{rows ? `${row.count} · ${row.percent.toFixed(1)}%` : '—'}</strong>
          </div>
        ))}
      </div>
    </article>
  );
}

function formatLakhs(value: number) {
  return `₹ ${(value / 100_000).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L`;
}

function FcdrDropoutRevenueChart({ rows, summary }: {
  rows?: FcdrClassicResponse['charts']['dropoutRevenueByService'];
  summary?: FcdrClassicResponse['dropoutRevenueSummary'];
}) {
  const displayed = rows?.length ? rows : [{ key: 'pending', label: 'Awaiting data', patientCount: 0, procedureCount: 0, value: 0, percent: 0 }];

  return (
    <article className="fcdr-chart-card fcdr-clinical-insight-card fcdr-dropout-revenue-card">
      <div className="fcdr-card-heading">
        <div><strong>Revenue from Dropout Patients by Service</strong><span>Governed non-package patient collections plus Medics net revenue for the broader lapsed follow-up cohort through the report as-of date.</span></div>
        <span className="fcdr-pending-badge">{summary ? `${formatLakhs(summary.totalValue)} · ${summary.cohortPatients} patients` : 'Awaiting data'}</span>
      </div>
      <div className="fcdr-dropout-revenue-summary" aria-label="Dropout revenue summary">
        <span><strong>{summary?.cohortPatients ?? '—'}</strong>lapsed follow-up patients</span>
        <span><strong>{summary?.billedPatients ?? '—'}</strong>value-bearing follow-up patients</span>
        <span><strong>{summary ? formatLakhs(summary.totalValue) : '—'}</strong>non-package governed value</span>
        <span><strong>{summary ? `${summary.shareOfSelectedCohortPercent.toFixed(1)}%` : '—'}</strong>of selected cohort non-package value</span>
      </div>
      <div className="fcdr-dropout-revenue-chart" role="img" aria-label={summary ? `Dropout patient non-package billed value: ${formatLakhs(summary.totalValue)}` : 'Dropout patient revenue: governed data connection pending'}>
        {displayed.map((row, index) => (
          <div className="fcdr-dropout-revenue-row" key={row.key}>
            <span><strong>{row.label}</strong><small>{rows ? `${row.patientCount} patients · ${row.procedureCount} billed services` : 'Awaiting data'}</small></span>
            <div className="fcdr-dropout-revenue-track" aria-hidden="true"><i style={{ width: `${Math.max(0, Math.min(100, row.percent))}%`, backgroundColor: FCDR_PACKAGE_CHART_COLORS[index % FCDR_PACKAGE_CHART_COLORS.length] }} /></div>
            <strong>{rows ? `${formatLakhs(row.value)} · ${row.percent.toFixed(1)}%` : '—'}</strong>
          </div>
        ))}
      </div>
    </article>
  );
}

function FcdrPackageConversionChart({ rows }: {
  rows?: Array<{ label: string; count: number; percent: number }>;
}) {
  const packages = rows ?? [{ label: 'Approved IVF package', count: 0, percent: 0 }];
  const total = rows?.reduce((sum, row) => sum + row.count, 0) ?? 0;

  return (
    <article className="fcdr-chart-card conversion fcdr-clinical-insight-card fcdr-package-conversion-card">
      <div className="fcdr-card-heading">
        <div><strong>Converted Patients by Package</strong><span>Approved IVF package enrolment among converted patients.</span></div>
        <span className="fcdr-pending-badge">{rows ? `${total} converted patients` : 'Awaiting data'}</span>
      </div>
      <div className="fcdr-stage-chart fcdr-package-chart" role="img" aria-label={rows ? `Converted patients by package: ${total} patients` : 'Converted patients by package: governed data connection pending'}>
        <div className="fcdr-package-axis" aria-hidden="true">
          <span />
          <div><span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span></div>
          <span />
        </div>
        {packages.map((row, index) => (
          <div className="fcdr-stage-row" key={row.label}>
            <span>{row.label}</span>
            <div className="fcdr-stage-track" aria-hidden="true"><i style={{ width: `${Math.max(0, Math.min(100, row.percent))}%`, backgroundColor: FCDR_PACKAGE_CHART_COLORS[index % FCDR_PACKAGE_CHART_COLORS.length] }} /></div>
            <strong>{rows ? `${row.count} · ${row.percent.toFixed(1)}%` : '—'}</strong>
          </div>
        ))}
      </div>
      {!rows ? <div className="fcdr-chart-footnote">Chart structure is ready for governed converted-patient package values.</div> : null}
    </article>
  );
}

function FcdrConversionMonthChart({ rows, timing }: {
  rows?: FcdrClassicResponse['charts']['conversionsByMonth'];
  timing?: FcdrClassicResponse['conversionTiming'];
}) {
  const displayed = rows?.length ? rows : [{ key: 'pending', label: 'Awaiting data', count: 0, percent: 0 }];
  const total = rows?.reduce((sum, row) => sum + row.count, 0) ?? 0;
  const chartConfig = useMemo(() => ({
    type: 'bar',
    plugins: [{
      id: 'fcdr-conversion-month-value-labels',
      afterDatasetsDraw: (chart: any) => {
        const { ctx, chartArea } = chart;
        ctx.save();
        ctx.fillStyle = document.body.classList.contains('dark-mode') ? '#e2e8f0' : '#334155';
        ctx.font = '700 11px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        chart.getDatasetMeta(0).data.forEach((bar: { x: number; y: number }, index: number) => {
          const row = displayed[index];
          if (!row?.count) return;
          ctx.fillText(`${row.count} · ${row.percent.toFixed(1)}%`, bar.x, Math.max(chartArea.top + 12, bar.y - 6));
        });
        ctx.restore();
      },
    }],
    data: {
      labels: displayed.map((row) => row.label),
      datasets: [{
        label: 'Converted patients',
        data: displayed.map((row) => row.count),
        backgroundColor: displayed.map((_row, index) => FCDR_PACKAGE_CHART_COLORS[index % FCDR_PACKAGE_CHART_COLORS.length]),
        hoverBackgroundColor: displayed.map((_row, index) => FCDR_PACKAGE_CHART_COLORS[(index + 1) % FCDR_PACKAGE_CHART_COLORS.length]),
        borderRadius: 7,
        maxBarThickness: 42,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 22 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context: { dataIndex: number; formattedValue: string }) => {
              const row = displayed[context.dataIndex];
              return `${context.formattedValue} converted patients · ${row?.percent.toFixed(1) ?? '0.0'}%`;
            },
          },
        },
      },
      scales: {
        y: { beginAtZero: true, grace: '10%', ticks: { precision: 0 } },
        x: { grid: { display: false }, ticks: { maxRotation: 0, minRotation: 0 } },
      },
    },
  }), [displayed]);

  return (
    <article className="fcdr-chart-card conversion fcdr-clinical-insight-card fcdr-clinical-volume-card fcdr-conversion-month-card">
      <div className="fcdr-card-heading">
        <div><strong>Converted Patients by Month</strong><span>Month of the first qualifying IVF package bill.</span></div>
        <span className="fcdr-pending-badge">{rows ? `${total} converted patients` : 'Awaiting data'}</span>
      </div>
      <div className="clinical-chart-body fcdr-conversion-month-chart-body" role="img" aria-label={rows ? `Converted patients by month: ${total} patients` : 'Converted patients by month: governed data connection pending'}>
        {rows?.length
          ? <FcdrChartCanvas id="fcdr-conversion-month-chart" config={chartConfig} />
          : <div className="clinical-chart-empty"><strong>No conversion data</strong><span>No qualifying IVF package bills match the selected FCDR filters.</span></div>}
      </div>
      {timing ? (
        <div className="fcdr-conversion-timing-summary">
          <span><strong>{timing.sameRegistrationMonth}</strong> in registration month</span>
          <span><strong>{timing.afterRegistrationMonth}</strong> after registration month</span>
          <span><strong>{timing.after90Days}</strong> after 90 days</span>
          <span><strong>{timing.latestPackageBillDate || '—'}</strong> latest conversion</span>
        </div>
      ) : null}
    </article>
  );
}

function FcdrTableCard({
  title,
  description,
  columns,
  emptyMessage,
  className = '',
  rows = [],
  total = rows.length,
  actions,
  footer,
  columnClassNames,
  summaryRow,
}: {
  title: string;
  description: string;
  columns: string[];
  emptyMessage: string;
  className?: string;
  rows?: ReactNode[][];
  total?: number;
  actions?: ReactNode;
  footer?: ReactNode;
  columnClassNames?: string[];
  summaryRow?: ReactNode[];
}) {
  return (
    <article className={`fcdr-table-card ${className}`.trim()}>
      <div className="fcdr-card-heading fcdr-table-heading">
        <div><strong>{title}</strong><span>{description}</span></div>
        <div className="fcdr-table-heading-actions">{actions}<span className="fcdr-pending-badge">{total} records</span></div>
      </div>
      <div className="fcdr-table-scroll">
        <table className="fcdr-data-table">
          <thead><tr>{columns.map((column, columnIndex) => <th className={columnClassNames?.[columnIndex]} key={column}>{column}</th>)}</tr></thead>
          <tbody>{rows.length ? rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td className={columnClassNames?.[cellIndex]} key={cellIndex}>{cell}</td>)}</tr>) : <tr><td className="fcdr-table-empty" colSpan={columns.length}><strong>No FCDR data loaded</strong><span>{emptyMessage}</span></td></tr>}</tbody>
          {rows.length && summaryRow ? <tfoot><tr>{summaryRow.map((cell, cellIndex) => <td className={columnClassNames?.[cellIndex]} key={cellIndex}>{cell}</td>)}</tr></tfoot> : null}
        </table>
      </div>
      {footer ? <div className="fcdr-table-footer">{footer}</div> : null}
    </article>
  );
}

function FcdrReturnIntervals({ rows = [], total = 0 }: {
  rows?: FcdrOutcomeV2Response['returnIntervals'];
  total?: number;
}) {
  const displayed = rows.length ? rows : returnIntervalLabels.map((label) => ({ key: label, label, count: 0, percent: 0 }));
  return (
    <article className="fcdr-chart-card fcdr-return-card">
      <div className="fcdr-card-heading">
        <div><strong>When Patients Returned</strong><span>Time from registration to second attended visit</span></div>
        <span className="fcdr-pending-badge">{total} patients</span>
      </div>
      <div className="fcdr-return-intervals" role="img" aria-label={`Return intervals for ${total} registered patients`}>
        {displayed.map((row) => (
          <div className="fcdr-return-row" key={row.key}>
            <span>{row.label}</span>
            <div className="fcdr-stage-track" aria-hidden="true"><i style={{ width: `${Math.max(0, Math.min(100, row.percent))}%` }} /></div>
            <strong>{row.count} · {row.percent.toFixed(1)}%</strong>
          </div>
        ))}
      </div>
    </article>
  );
}

function FcdrKeyResults({ data }: { data: FcdrOutcomeV2Response | null }) {
  const days = (value: number | null) => value === null ? '—' : `${value} days`;
  const results = data ? [
    ['Second-visit return performance', `${data.returnSummary.reached} (${data.returnSummary.percentage.toFixed(1)}%) · median ${days(data.returnSummary.medianDays)}`],
    ['Conversion timing and outcome mix', `${data.conversionSummary.count} (${data.conversionSummary.percentage.toFixed(1)}%) · median ${days(data.conversionSummary.medianDays)}`],
    ['Patients requiring attention', `${data.attention.count} active patients · inactive for ${data.attention.fromDays}+ days`],
    ['Reactivation evidence', `${data.reactivationSummary.count} (${data.reactivationSummary.percentage.toFixed(1)}%) · median gap ${days(data.reactivationSummary.medianGapDays)}`],
  ] : [
    ['Second-visit return performance', 'Awaiting data'],
    ['Conversion timing and outcome mix', 'Awaiting data'],
    ['Patients requiring attention', 'Awaiting data'],
    ['Reactivation evidence', 'Awaiting data'],
  ];
  return (
    <article className="fcdr-chart-card fcdr-key-results-card">
      <div className="fcdr-card-heading">
        <div><strong>Key Results</strong><span>Management summary for the selected registration cohort</span></div>
        <span className="fcdr-pending-badge">{data ? 'Governed' : 'Awaiting data'}</span>
      </div>
      <ol className="fcdr-key-results-list">
        {results.map(([label, value]) => <li key={label}><span>{label}</span><strong>{value}</strong></li>)}
      </ol>
    </article>
  );
}

export function FcdrReportTab({ active, centers, token, canExport = false }: { active: boolean; centers: string[]; token?: string; canExport?: boolean }) {
  const [view, setView] = useState<FcdrView>('classic');
  const [outcomeSubview, setOutcomeSubview] = useState<OutcomeSubview>('outcomes');
  const [classicFilters, setClassicFilters] = useState<ClassicFilters>(classicInitial);
  const [appliedClassicFilters, setAppliedClassicFilters] = useState<ClassicFilters>(classicInitial);
  const [outcomeFilters, setOutcomeFilters] = useState<OutcomeFilters>(outcomeInitial);
  const [appliedOutcomeFilters, setAppliedOutcomeFilters] = useState<OutcomeFilters>(outcomeInitial);
  const [classicData, setClassicData] = useState<FcdrClassicResponse | null>(null);
  const [classicState, setClassicState] = useState<FcdrDataState>('NOT_LOADED');
  const [classicError, setClassicError] = useState('');
  const [outcomeData, setOutcomeData] = useState<FcdrOutcomeV2Response | null>(null);
  const [outcomeState, setOutcomeState] = useState<FcdrDataState>('NOT_LOADED');
  const [outcomeError, setOutcomeError] = useState('');
  const [outcomePage, setOutcomePage] = useState(1);
  const [outcomePatientFilters, setOutcomePatientFilters] = useState<OutcomePatientTableFilters>(outcomePatientInitial);
  const [appliedOutcomePatientFilters, setAppliedOutcomePatientFilters] = useState<OutcomePatientTableFilters>(outcomePatientInitial);
  const [centreVisitColumn, setCentreVisitColumn] = useState<CentreVisitColumn>('ALL');
  const [patientCategory, setPatientCategory] = useState('ALL');
  const [patientPackageBill, setPatientPackageBill] = useState('ALL');
  const [patientPage, setPatientPage] = useState(1);
  const classicRequestId = useRef(0);
  const outcomeRequestId = useRef(0);
  const centreOptions = useMemo(
    () => [...new Set(centers.filter(Boolean))].sort((left, right) => left.localeCompare(right)),
    [centers],
  );
  const changeClassicPeriod = async (period: ClassicFilters['period']) => {
    if (period === 'MONTH') {
      setClassicFilters((current) => ({ ...current, period, from: '', to: '' }));
      return;
    }
    try {
      const range = await resolveAnalyticalYearPeriod(period, token);
      setClassicFilters((current) => ({ ...current, period, ...range }));
    } catch (error) {
      setClassicError(error instanceof Error ? error.message : 'Unable to resolve the selected reporting period.');
    }
  };
  const changeOutcomePeriod = async (period: OutcomeFilters['period']) => {
    if (period === 'MONTH') {
      setOutcomeFilters((current) => ({ ...current, period, from: '', to: '' }));
      return;
    }
    try {
      const range = await resolveAnalyticalYearPeriod(period, token);
      setOutcomeFilters((current) => ({ ...current, period, ...range }));
    } catch (error) {
      setOutcomeError(error instanceof Error ? error.message : 'Unable to resolve the selected reporting period.');
    }
  };
  const queryFor = useCallback((filters: ClassicFilters, table: PatientTableQuery): FcdrClassicQuery => ({
    ...(filters.period === 'MONTH' ? { month: filters.month } : { from: filters.from, to: filters.to }),
    centers: filters.centre ? [filters.centre] : undefined,
    ageing: filters.ageing,
    search: filters.search.trim() || undefined,
    patientCategory: table.category,
    packageBill: table.packageBill,
    skip: (table.page - 1) * PATIENT_PAGE_SIZE,
    take: PATIENT_PAGE_SIZE,
  }), []);
  const loadClassic = useCallback(async (filters: ClassicFilters, table: PatientTableQuery) => {
    const requestId = classicRequestId.current + 1;
    classicRequestId.current = requestId;
    setClassicState('LOADING');
    setClassicError('');
    try {
      const response = await getFcdrClassicReport(token, queryFor(filters, table));
      if (requestId !== classicRequestId.current) return;
      setClassicData(response);
      setClassicState(response.status);
    } catch (error) {
      if (requestId !== classicRequestId.current) return;
      setClassicData(null);
      setClassicError(error instanceof Error ? error.message : 'Unable to load Classic FCDR data.');
      setClassicState('ERROR');
    }
  }, [queryFor, token]);
  useEffect(() => {
    if (active && view === 'classic' && classicState === 'NOT_LOADED') {
      void loadClassic(classicFilters, { page: 1, category: 'ALL', packageBill: 'ALL' });
    }
  }, [active, classicFilters, classicState, loadClassic, view]);

  const outcomeQueryFor = useCallback((filters: OutcomeFilters, page: number, table: OutcomePatientTableFilters): FcdrOutcomeV2Query => ({
    ...(filters.period === 'MONTH' ? { month: filters.month } : { from: filters.from, to: filters.to }),
    centers: filters.centre ? [filters.centre] : undefined,
    outcome: filters.outcome === 'ALL' ? undefined : filters.outcome as FcdrOutcomeV2Query['outcome'],
    returnInterval: filters.returnInterval === 'ALL' ? undefined : filters.returnInterval as FcdrOutcomeV2Query['returnInterval'],
    attention: filters.attention === 'ALL' ? undefined : filters.attention as FcdrOutcomeV2Query['attention'],
    search: filters.search.trim() || undefined,
    patientCenters: table.centre ? [table.centre] : undefined,
    patientStatus: table.status === 'ALL' ? undefined : table.status,
    patientSearch: table.search.trim() || undefined,
    skip: (page - 1) * PATIENT_PAGE_SIZE,
    take: PATIENT_PAGE_SIZE,
  }), []);
  const loadOutcome = useCallback(async (filters: OutcomeFilters, page = 1, table = appliedOutcomePatientFilters) => {
    const requestId = outcomeRequestId.current + 1;
    outcomeRequestId.current = requestId;
    setOutcomeState('LOADING');
    setOutcomeError('');
    try {
      const response = await getFcdrOutcomeV2Report(token, outcomeQueryFor(filters, page, table));
      if (requestId !== outcomeRequestId.current) return;
      setOutcomeData(response);
      setOutcomePage(page);
      setOutcomeState(response.status);
    } catch (error) {
      if (requestId !== outcomeRequestId.current) return;
      setOutcomeData(null);
      setOutcomeError(error instanceof Error ? error.message : 'Unable to load Outcome V2 data.');
      setOutcomeState('ERROR');
    }
  }, [appliedOutcomePatientFilters, outcomeQueryFor, token]);
  useEffect(() => {
    if (active && view === 'outcome' && outcomeState === 'NOT_LOADED') void loadOutcome(outcomeFilters, 1, appliedOutcomePatientFilters);
  }, [active, appliedOutcomePatientFilters, loadOutcome, outcomeFilters, outcomeState, view]);

  const kpiValues = classicData ? [
    classicData.kpis.totalRegistered,
    classicData.kpis.age0To30,
    classicData.kpis.age31To60,
    classicData.kpis.age61To90,
    classicData.kpis.age90Plus,
    classicData.kpis.activeFollowUp,
    classicData.kpis.converted,
    classicData.kpis.dropout,
  ] : [];
  const totalRegistered = classicData?.kpis.totalRegistered ?? 0;
  const registeredShare = (percentage: number | undefined) => totalRegistered > 0
    ? `${percentage ?? 0}% of registered patients`
    : 'Percentage of registered patients';
  const classicKpiCopy = [
    { description: '100% of registered patients' },
    {
      description: registeredShare(classicData?.kpiPercentages.age0To30),
      detail: '0–30 calendar days',
      note: <>Registration <b>→</b> 2nd visit</>,
    },
    {
      description: registeredShare(classicData?.kpiPercentages.age31To60),
      detail: '31–60 calendar days',
      note: <>Registration <b>→</b> 2nd visit</>,
    },
    {
      description: registeredShare(classicData?.kpiPercentages.age61To90),
      detail: '61–90 calendar days',
      note: <>Registration <b>→</b> 2nd visit</>,
    },
    {
      description: registeredShare(classicData?.kpiPercentages.age90Plus),
      detail: 'More than 90 calendar days',
      note: <>Registration <b>→</b> 2nd visit</>,
    },
    {
      description: registeredShare(classicData?.kpiPercentages.activeFollowUp),
      detail: `Non-converted patients with a successful visit in the last 90 days as of ${reportDate(classicData?.asOfDate)}.`,
      note: 'Exclusive current follow-up category',
    },
    {
      description: registeredShare(classicData?.kpiPercentages.converted),
      detail: 'IVF Package Billed',
    },
    {
      description: registeredShare(classicData?.kpiPercentages.dropout),
      detail: 'No second visit for more than 90 days from date of registration',
    },
  ];
  const dash = (value: string | null | undefined) => value || '—';
  const allCentreRows: ReactNode[][] = (classicData?.centreRows ?? []).map((row) => [
    shortCentreName(row.center), row.total, row.secondVisit, row.thirdVisit, row.fourthVisit, row.fifthPlusVisit,
    row.age0To30, row.age31To60, row.age61To90, row.age90Plus, row.activeFollowUp, row.converted, row.dropout,
  ]);
  const centreColumnIndexes = centreVisitColumn === 'SECOND' ? [0, 1, 2, 6, 7, 8, 9, 10, 11, 12]
    : centreVisitColumn === 'THIRD' ? [0, 1, 3, 6, 7, 8, 9, 10, 11, 12]
      : centreVisitColumn === 'FOURTH' ? [0, 1, 4, 6, 7, 8, 9, 10, 11, 12]
        : centreVisitColumn === 'FIFTH_PLUS' ? [0, 1, 5, 6, 7, 8, 9, 10, 11, 12]
          : centreBreakdownColumns.map((_column, index) => index);
  const centreColumns = centreColumnIndexes.map((index) => centreBreakdownColumns[index]);
  const centreRows = allCentreRows.map((row) => centreColumnIndexes.map((index) => row[index]));
  const allCentreColumnClassNames = [
    'fcdr-centre-name', 'fcdr-centre-total fcdr-numeric',
    'fcdr-visit-second fcdr-numeric', 'fcdr-visit-third fcdr-numeric',
    'fcdr-visit-fourth fcdr-numeric', 'fcdr-visit-fifth-plus fcdr-numeric',
    'fcdr-age-0-30 fcdr-numeric', 'fcdr-age-31-60 fcdr-numeric',
    'fcdr-age-61-90 fcdr-numeric', 'fcdr-age-90-plus fcdr-numeric',
    'fcdr-active-follow-up fcdr-numeric', 'fcdr-converted fcdr-numeric', 'fcdr-dropout fcdr-numeric',
  ];
  const centreColumnClassNames = centreColumnIndexes.map((index) => allCentreColumnClassNames[index]);
  const centreVisitTotals = (classicData?.centreRows ?? []).reduce((totals, row) => ({
    secondVisit: totals.secondVisit + row.secondVisit,
    thirdVisit: totals.thirdVisit + row.thirdVisit,
    fourthVisit: totals.fourthVisit + row.fourthVisit,
    fifthPlusVisit: totals.fifthPlusVisit + row.fifthPlusVisit,
  }), { secondVisit: 0, thirdVisit: 0, fourthVisit: 0, fifthPlusVisit: 0 });
  const allCentreSummaryRow: ReactNode[] = [
    'Total', classicData?.kpis.totalRegistered ?? 0,
    centreVisitTotals.secondVisit, centreVisitTotals.thirdVisit,
    centreVisitTotals.fourthVisit, centreVisitTotals.fifthPlusVisit,
    classicData?.kpis.age0To30 ?? 0, classicData?.kpis.age31To60 ?? 0,
    classicData?.kpis.age61To90 ?? 0, classicData?.kpis.age90Plus ?? 0,
    classicData?.kpis.activeFollowUp ?? 0, classicData?.kpis.converted ?? 0, classicData?.kpis.dropout ?? 0,
  ];
  const centreSummaryRow = centreColumnIndexes.map((index) => allCentreSummaryRow[index]);
  const packageOptions = classicData?.packageBillOptions ?? [];
  const patientRows: ReactNode[][] = (classicData?.patientRows ?? []).map((row) => [
    row.patientId, row.patientName, shortCentreName(row.center), row.registrationDate, dash(row.firstAppointmentDate),
    dash(row.latestAppointmentDate), dash(row.secondVisitDate), dash(row.thirdVisitDate),
    dash(row.fourthVisitDate), dash(row.fifthPlusVisitDate),
    <span className="fcdr-package-bill" key={`${row.patientId}-package`}><strong>{dash(row.packageBillName)}</strong>{row.packageBillDate ? <small>{row.packageBillDate}</small> : null}</span>,
    row.ageingDays ?? '—',
    classicCategoryLabel(row.category),
  ]);
  const patientTotal = classicData?.pagination.total ?? 0;
  const patientPageCount = Math.max(1, Math.ceil(patientTotal / PATIENT_PAGE_SIZE));
  const patientRangeStart = patientTotal ? (classicData?.pagination.skip ?? 0) + 1 : 0;
  const patientRangeEnd = Math.min(patientTotal, (classicData?.pagination.skip ?? 0) + (classicData?.patientRows.length ?? 0));
  const loadPatientPage = (requestedPage: number) => {
    const page = Math.max(1, Math.min(patientPageCount, requestedPage));
    setPatientPage(page);
    void loadClassic(appliedClassicFilters, { page, category: patientCategory, packageBill: patientPackageBill });
  };

  const outcomeKpiValues = outcomeData ? [
    outcomeData.kpis.totalRegistered,
    outcomeData.kpis.active,
    outcomeData.kpis.dropout,
    outcomeData.kpis.converted,
  ] : [];
  const outcomeKpiDescriptions = outcomeData ? [
    '100% of registered patients',
    `${outcomeData.percentages.active.toFixed(1)}% of registered patients`,
    `${outcomeData.percentages.dropout.toFixed(1)}% of registered patients`,
    `${outcomeData.percentages.converted.toFixed(1)}% of registered patients`,
  ] : outcomeKpis.map((kpi) => kpi.description);
  const intelligenceKpiValues = outcomeData ? [
    outcomeData.kpis.totalRegistered,
    outcomeData.kpis.active,
    outcomeData.kpis.returned,
    outcomeData.kpis.converted,
    outcomeData.kpis.dropout,
  ] : [];
  const intelligenceKpiDescriptions = outcomeData ? [
    '100% of registered patients',
    `${outcomeData.percentages.active.toFixed(1)}% of registered patients`,
    `${outcomeData.percentages.returned.toFixed(1)}% of registered patients`,
    `${outcomeData.percentages.converted.toFixed(1)}% of registered patients`,
    `${outcomeData.percentages.dropout.toFixed(1)}% of registered patients`,
  ] : intelligenceKpis.map((kpi) => kpi.description);
  const outcomeCentreRows: ReactNode[][] = (outcomeData?.centreRows ?? []).map((row) => [
    shortCentreName(row.center), row.registered, row.active, row.dropout, row.converted,
  ]);
  const outcomeCentreSummary: ReactNode[] = [
    'Total', outcomeData?.kpis.totalRegistered ?? 0, outcomeData?.kpis.active ?? 0,
    outcomeData?.kpis.dropout ?? 0, outcomeData?.kpis.converted ?? 0,
  ];
  const outcomePatientRows: ReactNode[][] = (outcomeData?.patientRows ?? []).map((row) => [
    row.patientId,
    row.patientName,
    shortCentreName(row.center),
    row.registrationDate,
    <span className="fcdr-care-activity" key={`${row.patientId}-activity`}>
      <strong>{row.careActivity}</strong>
      <small>2nd visit: {dash(row.secondVisitDate)} · Latest: {row.latestActivityDate}</small>
    </span>,
    <span className="fcdr-package-bill" key={`${row.patientId}-outcome-package`}>
      <strong>{dash(row.packageBillName)}</strong>
      {row.packageBillDate ? <small>{row.packageBillDate}</small> : null}
    </span>,
    <span className={`fcdr-outcome-status ${row.status.toLocaleLowerCase('en-IN')}`} key={`${row.patientId}-status`}>{row.status}</span>,
    row.consideration,
  ]);
  const intelligenceCentreRows: ReactNode[][] = (outcomeData?.centreRows ?? []).map((row) => [
    shortCentreName(row.center),
    row.registered,
    `${row.returned} · ${row.returnPercentage.toFixed(1)}%`,
    row.medianReturnDays === null ? '—' : `${row.medianReturnDays} days`,
    row.active,
    `${row.dropout} · ${row.dropoutPercentage.toFixed(1)}%`,
    `${row.converted} · ${row.conversionPercentage.toFixed(1)}%`,
  ]);
  const intelligenceCentreSummary: ReactNode[] = [
    'Total',
    outcomeData?.kpis.totalRegistered ?? 0,
    outcomeData ? `${outcomeData.kpis.returned} · ${outcomeData.percentages.returned.toFixed(1)}%` : '—',
    outcomeData?.returnSummary.medianDays === null || outcomeData?.returnSummary.medianDays === undefined
      ? '—' : `${outcomeData.returnSummary.medianDays} days`,
    outcomeData?.kpis.active ?? 0,
    outcomeData ? `${outcomeData.kpis.dropout} · ${outcomeData.percentages.dropout.toFixed(1)}%` : '—',
    outcomeData ? `${outcomeData.kpis.converted} · ${outcomeData.percentages.converted.toFixed(1)}%` : '—',
  ];
  const outcomeTotal = outcomeData?.pagination.total ?? 0;
  const outcomePageCount = Math.max(1, Math.ceil(outcomeTotal / PATIENT_PAGE_SIZE));
  const outcomeRangeStart = outcomeTotal ? (outcomeData?.pagination.skip ?? 0) + 1 : 0;
  const outcomeRangeEnd = Math.min(outcomeTotal, (outcomeData?.pagination.skip ?? 0) + (outcomeData?.patientRows.length ?? 0));
  const loadOutcomePage = (requestedPage: number) => {
    const page = Math.max(1, Math.min(outcomePageCount, requestedPage));
    void loadOutcome(appliedOutcomeFilters, page, appliedOutcomePatientFilters);
  };

  const applyOutcomePatientFilters = (filters: OutcomePatientTableFilters) => {
    setOutcomePatientFilters(filters);
    setAppliedOutcomePatientFilters(filters);
    setOutcomePage(1);
    void loadOutcome(appliedOutcomeFilters, 1, filters);
  };

  return (
    <section className="tab-content enterprise-compact enterprise-page fcdr-report-page" style={{ display: active ? 'grid' : 'none' }}>
      <div className="doctor-report-tabs fcdr-view-tabs" role="tablist" aria-label="FCDR report views">
        <button type="button" role="tab" aria-selected={view === 'classic'} className={view === 'classic' ? 'active' : ''} onClick={() => setView('classic')}>
          <span className="doctor-report-tab-icon fcdr-view-icon"><ReportIcon kind="classic" /></span>
          <span><strong>Classic FCDR</strong><small>Operational follow-up monitoring</small></span>
        </button>
        <button type="button" role="tab" aria-selected={view === 'outcome'} className={view === 'outcome' ? 'active' : ''} onClick={() => setView('outcome')}>
          <span className="doctor-report-tab-icon fcdr-view-icon"><ReportIcon kind="outcome" /></span>
          <span><strong>Outcome V2</strong><small>Management outcome intelligence</small></span>
        </button>
      </div>

      {view === 'classic' ? (
        <div className="fcdr-workspace" role="tabpanel" aria-label="Classic FCDR">
          <div className="card enterprise-filter-card fcdr-filter-card">
            <div className="fcdr-filter-heading">
              <div><strong>Classic FCDR filters</strong><span>Scope follow-up ageing, visit progression and patient-level operational evidence.</span></div>
              <div className="fcdr-filter-status-actions">
                {canExport ? <button type="button" className="btn btn-outline compact-btn" disabled={!classicData || classicState === 'LOADING'} onClick={() => void exportFcdrClassicReport(token, queryFor(appliedClassicFilters, { page: 1, category: patientCategory, packageBill: patientPackageBill }))}>Export Excel</button> : null}
                <FcdrDataStatus state={classicState} title={classicError || classicData?.coverage.message || 'Governed FCDR data status'} />
              </div>
            </div>
            <div className="enterprise-filter-grid two-line-filter-grid tracker-filter-grid fcdr-filter-grid classic">
              <label className="enterprise-field"><span>Registration period</span><select value={classicFilters.period} onChange={(event) => void changeClassicPeriod(event.target.value as ClassicFilters['period'])}><option value="MONTH">Selected month</option><option value="YTD">YTD (Year to Date)</option><option value="FY_YTD">FY-YTD (Financial Year to Date)</option></select></label>
              {classicFilters.period === 'MONTH' ? <label className="enterprise-field"><span>Registration month</span><input type="month" value={classicFilters.month} onChange={(event) => setClassicFilters((current) => ({ ...current, month: event.target.value }))} /></label> : <label className="enterprise-field"><span>Resolved dates</span><input value={`${classicFilters.from} to ${classicFilters.to}`} readOnly /></label>}
              <label className="enterprise-field"><span>Centre / location</span><select value={classicFilters.centre} onChange={(event) => setClassicFilters((current) => ({ ...current, centre: event.target.value }))}><option value="">All permitted centres</option>{centreOptions.map((centre) => <option key={centre} value={centre}>{centre}</option>)}</select></label>
              <label className="enterprise-field"><span>Ageing category</span><select value={classicFilters.ageing} onChange={(event) => setClassicFilters((current) => ({ ...current, ageing: event.target.value }))}><option value="ALL">All categories</option><option value="CONVERTED">Converted</option><option value="0_30">0–30 days</option><option value="31_60">31–60 days</option><option value="61_90">61–90 days</option><option value="90_PLUS">90+ days</option><option value="DROPOUT">Dropout</option></select></label>
              <label className="enterprise-field fcdr-search-field"><span>Patient search</span><input type="search" placeholder="Name, patient ID, MRN, centre, category or package" value={classicFilters.search} onChange={(event) => setClassicFilters((current) => ({ ...current, search: event.target.value }))} /></label>
              <button type="button" className="btn btn-outline compact-btn filter-action" disabled={classicState === 'LOADING'} onClick={() => { const reset = classicInitial(); setClassicFilters(reset); setAppliedClassicFilters(reset); setCentreVisitColumn('ALL'); setPatientCategory('ALL'); setPatientPackageBill('ALL'); setPatientPage(1); void loadClassic(reset, { page: 1, category: 'ALL', packageBill: 'ALL' }); }}>Clear Filters</button>
              <button type="button" className="btn btn-primary compact-btn filter-action" disabled={classicState === 'LOADING'} onClick={() => { setAppliedClassicFilters(classicFilters); setPatientPage(1); void loadClassic(classicFilters, { page: 1, category: patientCategory, packageBill: patientPackageBill }); }}>{classicState === 'LOADING' ? 'Loading…' : 'Apply Filters'}</button>
            </div>
            <div className="fcdr-filter-note">Classic filters remain independent from Outcome V2.</div>
          </div>

          <div className="fcdr-classic-content">
            <div className="fcdr-method-note">
              <strong>How counts reconcile</strong>
              <span>Every patient appears in one KPI category only. During the first 90 days after registration, patients with a second attended visit remain in its timing band and patients without one are Active Follow-up. After 90 days, a recent successful visit moves a non-converted patient to Active Follow-up; without a second visit the patient becomes a dropout.</span>
            </div>

            <div className="fcdr-kpi-grid" aria-label="Classic FCDR KPI cards">
              {classicKpis.map((kpi, index) => <FcdrKpiCard key={kpi.title} {...kpi} {...classicKpiCopy[index]} value={kpiValues[index] ?? '—'} />)}
            </div>

            <div className="fcdr-chart-grid">
              <FcdrDropoutStatusChart rows={classicData?.charts.dropoutsByAppointmentStatus} />
              <FcdrStageChart title="Lapsed Follow-up Patients by Last Visit Stage" description="Non-converted patients with no attended visit in the last 90 days" tone="dropout" rows={classicData?.charts.lapsedFollowUpByVisitStage} />
            </div>

            <div className="fcdr-table-stack">
              <FcdrStageChart title="Conversions by Visit Stage" description="Visit stage reached by the qualifying package bill date" tone="conversion" rows={classicData?.charts.conversionsByVisitStage} />
              <FcdrDropoutRevenueChart rows={classicData?.charts.dropoutRevenueByService} summary={classicData?.dropoutRevenueSummary} />
              <FcdrTableCard className="fcdr-centre-breakdown" title="Centre-wise Breakdown" description="Active ageing, including Converted, and Dropout counts by centre" columns={centreColumns} columnClassNames={centreColumnClassNames} rows={centreRows} summaryRow={centreSummaryRow} emptyMessage="No centre-wise FCDR values match the selected filters." actions={<label className="fcdr-table-control"><span>Show visit column</span><select value={centreVisitColumn} onChange={(event) => setCentreVisitColumn(event.target.value as CentreVisitColumn)}><option value="ALL">All visit columns</option><option value="SECOND">2nd visit</option><option value="THIRD">3rd visit</option><option value="FOURTH">4th visit</option><option value="FIFTH_PLUS">5th+ visit</option></select></label>} />
              <FcdrConversionMonthChart rows={classicData?.charts.conversionsByMonth} timing={classicData?.conversionTiming} />
              <FcdrPackageConversionChart rows={classicData?.charts.conversionsByPackage} />
              <FcdrTableCard
                className="fcdr-patient-details"
                title="Patient Details"
                description="Visit dates, registration-to-second-visit days, and category"
                columns={patientDetailColumns}
                columnClassNames={patientDetailColumnClassNames}
                rows={patientRows}
                total={patientTotal}
                emptyMessage="No patient-level FCDR values match the selected filters."
                actions={<>
                  <label className="fcdr-table-control"><span>Category</span><select value={patientCategory} onChange={(event) => { const category = event.target.value; setPatientCategory(category); setPatientPage(1); void loadClassic(appliedClassicFilters, { page: 1, category, packageBill: patientPackageBill }); }}><option value="ALL">All categories</option><option value="CONVERTED">Converted</option><option value="ACTIVE_FOLLOW_UP">Active Follow-up</option><option value="0_30">0–30 days</option><option value="31_60">31–60 days</option><option value="61_90">61–90 days</option><option value="90_PLUS">90+ days</option><option value="DROPOUT">Dropout</option></select></label>
                  <label className="fcdr-table-control"><span>Package bill</span><select value={patientPackageBill} onChange={(event) => { const packageBill = event.target.value; setPatientPackageBill(packageBill); setPatientPage(1); void loadClassic(appliedClassicFilters, { page: 1, category: patientCategory, packageBill }); }}><option value="ALL">All package bills</option><option value="NONE">No approved package bill</option>{packageOptions.map((name) => <option key={name} value={name}>{name}</option>)}</select></label>
                </>}
                footer={<>
                  <span>{patientTotal ? `${patientRangeStart}–${patientRangeEnd} of ${patientTotal}` : '0 records'} · {PATIENT_PAGE_SIZE} per page</span>
                  <div className="fcdr-pagination-controls">
                    <button type="button" className="btn btn-outline compact-btn" disabled={patientPage <= 1 || classicState === 'LOADING'} onClick={() => loadPatientPage(patientPage - 1)}>Previous</button>
                    <span>Page {patientPage} of {patientPageCount}</span>
                    <button type="button" className="btn btn-outline compact-btn" disabled={patientPage >= patientPageCount || classicState === 'LOADING'} onClick={() => loadPatientPage(patientPage + 1)}>Next</button>
                  </div>
                </>}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="fcdr-workspace" role="tabpanel" aria-label="Outcome V2">
          <div className="card enterprise-filter-card fcdr-filter-card">
            <div className="fcdr-filter-heading">
              <div><strong>Outcome V2 filters</strong><span>Scope exclusive patient outcomes, return behaviour and management attention.</span></div>
              <FcdrDataStatus state={outcomeState} title={outcomeError || outcomeData?.coverage.message || 'Governed Outcome V2 data status'} />
            </div>
            <div className="enterprise-filter-grid two-line-filter-grid tracker-filter-grid fcdr-filter-grid outcome">
              <label className="enterprise-field"><span>Registration period</span><select value={outcomeFilters.period} onChange={(event) => void changeOutcomePeriod(event.target.value as OutcomeFilters['period'])}><option value="MONTH">Selected month</option><option value="YTD">YTD (Year to Date)</option><option value="FY_YTD">FY-YTD (Financial Year to Date)</option></select></label>
              {outcomeFilters.period === 'MONTH' ? <label className="enterprise-field"><span>Registration month</span><input type="month" value={outcomeFilters.month} onChange={(event) => setOutcomeFilters((current) => ({ ...current, month: event.target.value }))} /></label> : <label className="enterprise-field"><span>Resolved dates</span><input value={`${outcomeFilters.from} to ${outcomeFilters.to}`} readOnly /></label>}
              <label className="enterprise-field"><span>Centre / location</span><select value={outcomeFilters.centre} onChange={(event) => setOutcomeFilters((current) => ({ ...current, centre: event.target.value }))}><option value="">All permitted centres</option>{centreOptions.map((centre) => <option key={centre} value={centre}>{centre}</option>)}</select></label>
              <label className="enterprise-field"><span>Patient outcome</span><select value={outcomeFilters.outcome} onChange={(event) => setOutcomeFilters((current) => ({ ...current, outcome: event.target.value }))}><option value="ALL">All outcomes</option><option value="ACTIVE">Active</option><option value="DROPOUT">Dropout</option><option value="CONVERTED">Converted</option></select></label>
              <label className="enterprise-field"><span>Return interval</span><select value={outcomeFilters.returnInterval} onChange={(event) => setOutcomeFilters((current) => ({ ...current, returnInterval: event.target.value }))}><option value="ALL">All return intervals</option><option value="0_7">0–7 days</option><option value="8_14">8–14 days</option><option value="15_30">15–30 days</option><option value="31_60">31–60 days</option><option value="61_90">61–90 days</option><option value="91_PLUS">91+ days</option><option value="NO_RETURN">No return recorded</option></select></label>
              <label className="enterprise-field"><span>Attention classification</span><select value={outcomeFilters.attention} onChange={(event) => setOutcomeFilters((current) => ({ ...current, attention: event.target.value }))}><option value="ALL">All classifications</option><option value="ATTENTION">Requires attention</option><option value="REACTIVATED">Reactivated</option></select></label>
              <label className="enterprise-field fcdr-search-field"><span>Patient search</span><input type="search" placeholder="Name, patient ID, centre, outcome or package" value={outcomeFilters.search} onChange={(event) => setOutcomeFilters((current) => ({ ...current, search: event.target.value }))} /></label>
              <button type="button" className="btn btn-outline compact-btn filter-action" disabled={outcomeState === 'LOADING'} onClick={() => {
                const reset = outcomeInitial();
                const patientReset = outcomePatientInitial();
                setOutcomeFilters(reset);
                setAppliedOutcomeFilters(reset);
                setOutcomePatientFilters(patientReset);
                setAppliedOutcomePatientFilters(patientReset);
                setOutcomePage(1);
                void loadOutcome(reset, 1, patientReset);
              }}>Clear Filters</button>
              <button type="button" className="btn btn-primary compact-btn filter-action" disabled={outcomeState === 'LOADING'} onClick={() => { setAppliedOutcomeFilters(outcomeFilters); setOutcomePage(1); void loadOutcome(outcomeFilters, 1, appliedOutcomePatientFilters); }}>{outcomeState === 'LOADING' ? 'Loading…' : 'Apply Filters'}</button>
            </div>
            <div className="fcdr-filter-note">Outcome V2 filters remain independent from Classic FCDR.</div>
          </div>

          <div className="fcdr-outcome-subtabs" role="tablist" aria-label="Outcome V2 report sections">
            <button type="button" role="tab" aria-selected={outcomeSubview === 'outcomes'} className={outcomeSubview === 'outcomes' ? 'active' : ''} onClick={() => setOutcomeSubview('outcomes')}>
              <strong>Patient Outcomes</strong>
              <span>Exclusive patient classification and evidence</span>
            </button>
            <button type="button" role="tab" aria-selected={outcomeSubview === 'intelligence'} className={outcomeSubview === 'intelligence' ? 'active' : ''} onClick={() => setOutcomeSubview('intelligence')}>
              <strong>FCDR Intelligence</strong>
              <span>Monthly return, conversion and centre intelligence</span>
            </button>
          </div>

          {outcomeSubview === 'outcomes' ? (
            <div className="fcdr-outcome-panel" role="tabpanel" aria-label="Patient outcomes">
              <div className="fcdr-outcome-hero">
                <div><span>Patient outcomes</span><strong>Every registered patient is classified as Active, Dropout, or Converted using governed evidence.</strong></div>
                <span className="fcdr-pending-badge">As of {outcomeData?.asOfDate ?? '—'}</span>
              </div>

              <div className="fcdr-outcome-kpi-grid" aria-label="Patient outcome KPI cards">
                {outcomeKpis.map((kpi, index) => <FcdrKpiCard key={kpi.title} {...kpi} value={outcomeKpiValues[index] ?? '—'} description={outcomeKpiDescriptions[index]} detail={kpi.description} />)}
              </div>

              <div className="fcdr-table-stack">
                <FcdrTableCard className="fcdr-outcome-centre-breakdown" title="Centre-wise Outcome Breakdown" description="Registered patients and their current outcome by centre" columns={outcomeCentreColumns} columnClassNames={outcomeCentreColumnClassNames} rows={outcomeCentreRows} summaryRow={outcomeCentreSummary} emptyMessage="No centre outcome values match the selected filters." />
                <FcdrTableCard
                  className="fcdr-outcome-patient-records"
                  title="All Patient Records"
                  description="Patient-level outcome classification and governed evidence."
                  columns={outcomePatientColumns}
                  columnClassNames={outcomePatientColumnClassNames}
                  rows={outcomePatientRows}
                  total={outcomeTotal}
                  emptyMessage="No patient outcome records match the selected filters."
                  actions={<>
                    <label className="fcdr-table-control">
                      <span>Centre</span>
                      <select value={outcomePatientFilters.centre} onChange={(event) => applyOutcomePatientFilters({ ...outcomePatientFilters, centre: event.target.value })}>
                        <option value="">All centres</option>
                        {centreOptions.map((centre) => <option key={centre} value={centre}>{shortCentreName(centre)}</option>)}
                      </select>
                    </label>
                    <label className="fcdr-table-control">
                      <span>Status</span>
                      <select value={outcomePatientFilters.status} onChange={(event) => applyOutcomePatientFilters({ ...outcomePatientFilters, status: event.target.value as OutcomePatientTableFilters['status'] })}>
                        <option value="ALL">All statuses</option>
                        <option value="ACTIVE">Active</option>
                        <option value="DROPOUT">Dropout</option>
                        <option value="CONVERTED">Converted</option>
                      </select>
                    </label>
                    <label className="fcdr-table-control fcdr-table-search-control">
                      <span>Search</span>
                      <input
                        type="search"
                        value={outcomePatientFilters.search}
                        placeholder="Search all columns"
                        onChange={(event) => setOutcomePatientFilters((current) => ({ ...current, search: event.target.value }))}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') applyOutcomePatientFilters(outcomePatientFilters);
                        }}
                      />
                    </label>
                    <button type="button" className="btn btn-outline compact-btn fcdr-table-search-button" disabled={outcomeState === 'LOADING'} onClick={() => applyOutcomePatientFilters(outcomePatientFilters)}>Search</button>
                  </>}
                  footer={<>
                    <span>{outcomeTotal ? `${outcomeRangeStart}–${outcomeRangeEnd} of ${outcomeTotal}` : '0 records'} · {PATIENT_PAGE_SIZE} per page</span>
                    <div className="fcdr-pagination-controls">
                      <button type="button" className="btn btn-outline compact-btn" disabled={outcomePage <= 1 || outcomeState === 'LOADING'} onClick={() => loadOutcomePage(outcomePage - 1)}>Previous</button>
                      <span>Page {outcomePage} of {outcomePageCount}</span>
                      <button type="button" className="btn btn-outline compact-btn" disabled={outcomePage >= outcomePageCount || outcomeState === 'LOADING'} onClick={() => loadOutcomePage(outcomePage + 1)}>Next</button>
                    </div>
                  </>}
                />
              </div>
            </div>
          ) : (
            <div className="fcdr-outcome-panel" role="tabpanel" aria-label="FCDR Intelligence">
              <article className="fcdr-intelligence-brief">
                <div><span>Monthly Patient Summary</span><strong>Patients registered in the selected month</strong><small>{outcomeData ? `${outcomeData.period.from} to ${outcomeData.period.to} · classified through ${outcomeData.asOfDate}` : 'Choose a registration month to see the summary.'}</small></div>
                <span className="fcdr-pending-badge">{outcomeData ? `${outcomeData.kpis.totalRegistered} patients` : 'Waiting for data'}</span>
              </article>

              <div className="fcdr-outcome-kpi-grid intelligence" aria-label="FCDR Intelligence KPI cards">
                {intelligenceKpis.map((kpi, index) => <FcdrKpiCard key={kpi.title} {...kpi} value={intelligenceKpiValues[index] ?? '—'} description={intelligenceKpiDescriptions[index]} detail={kpi.description} />)}
              </div>

              <div className="fcdr-intelligence-grid">
                <FcdrReturnIntervals rows={outcomeData?.returnIntervals} total={outcomeData?.kpis.totalRegistered ?? 0} />
                <FcdrKeyResults data={outcomeData} />
              </div>

              <div className="fcdr-chart-grid">
                <FcdrStageChart title="Dropouts by Visit Stage" description="Last visit reached before 90+ days without an attended visit" tone="dropout" rows={outcomeData?.charts.dropoutsByVisitStage} />
                <FcdrStageChart title="Conversions by Visit Stage" description="Visit stage reached on or before the package bill date" tone="conversion" rows={outcomeData?.charts.conversionsByVisitStage} />
              </div>

              <FcdrTableCard title="Centre Results" description="Results by centre for patients registered in the selected month" columns={intelligenceCentreColumns} rows={intelligenceCentreRows} summaryRow={intelligenceCentreSummary} emptyMessage="No monthly centre intelligence matches the selected filters." />

              <div className="fcdr-outcome-method-note">
                <strong>Classification note</strong>
                <span>Reactivated means a patient attended again after a gap of more than 90 days. It does not mean the patient was previously marked Dropout. No patient names are shown.</span>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
